import type { DirectiveBinding } from 'vue'

export interface RichOverflowOptions {
  num: number
  ellipsis: string
  moreText: string
  lessText: string
  buttonClass?: string
  buttonStyle?: Partial<CSSStyleDeclaration>
  buttonContainerClass?: string
  buttonContainerStyle?: Partial<CSSStyleDeclaration>
  collapseMode: 'chars' | 'lines'
  defaultExpanded?: boolean // 新增默认展开字段
}

export const DEFAULT_OPTIONS: RichOverflowOptions = {
  num: 120,
  ellipsis: '...',
  moreText: '展开',
  lessText: '收起',
  collapseMode: 'chars',
  defaultExpanded: false, // 新增默认值
}

export function cleanRichText(text: string): string {
  // 先移除媒体标签
  const withoutMedia = text.replace(/<(img|video|audio)[^>]*>/gi, '')
  // 移除所有其他 HTML 标签
  return withoutMedia.replace(/<[^>]+>/g, '')
}

export function calculateAdjustedLength(text: string, num: number): number {
  const subText = text.slice(0, num)
  const n = (subText.match(/\d/g) || []).length
  const e = (subText.match(/[a-z]/gi) || []).length
  return (n + e > 0.7 * num) ? num + n + e : num
}

export function createActionButton(text: string, customClass?: string, customStyle?: Partial<CSSStyleDeclaration>): HTMLElement {
  const button = document.createElement('span')
  button.textContent = text
  button.className = 'rich-overflow-btn'
  button.style.cssText = 'color: #007bff; cursor: pointer; display: inline-block; white-space: nowrap; margin-left: 4px; vertical-align: baseline;'

  if (customStyle) {
    Object.assign(button.style, customStyle)
  }

  if (customClass) {
    button.classList.add(customClass)
  }

  return button
}

// 添加自定义元素接口
interface CustomHTMLElement extends HTMLElement {
  _cleanup?: () => void
}

// 添加状态管理
const elementStateMap = new WeakMap<HTMLElement, {
  originalText: string
  cleanedHtml: string // 存储移除媒体标签后的HTML
  cleanedText: string // 存储移除所有标签后的纯文本
  isExpanded: boolean
}>()

function initialize(el: CustomHTMLElement, binding: DirectiveBinding<Partial<RichOverflowOptions>>) {
  // 初始化元素状态
  const originalText = el.innerHTML
  const cleanedHtml = originalText.replace(/<(img|video|audio)[^>]*>/gi, '')
  const cleanedText = cleanRichText(originalText)

  el._cleanup = () => {
    el.removeEventListener('click', handleExpand)
    window.removeEventListener('resize', handleResize)
    elementStateMap.delete(el)
  }

  elementStateMap.set(el, {
    originalText,
    cleanedHtml, // 存储移除媒体标签后的HTML
    cleanedText, // 存储移除所有标签后的纯文本
    isExpanded: false,
  })

  const options: Required<RichOverflowOptions> = {
    num: binding.value?.num ?? DEFAULT_OPTIONS.num,
    ellipsis: binding.value?.ellipsis ?? DEFAULT_OPTIONS.ellipsis,
    moreText: binding.value?.moreText ?? DEFAULT_OPTIONS.moreText,
    lessText: binding.value?.lessText ?? DEFAULT_OPTIONS.lessText,
    buttonClass: binding.value?.buttonClass || '',
    buttonStyle: binding.value?.buttonStyle || '',
    buttonContainerClass: binding.value?.buttonContainerClass || '',
    buttonContainerStyle: binding.value?.buttonContainerStyle || '',
    collapseMode: binding.value?.collapseMode ?? DEFAULT_OPTIONS.collapseMode,
    defaultExpanded: binding.value?.defaultExpanded ?? (DEFAULT_OPTIONS.defaultExpanded || false),
  }

  const computedStyle = window.getComputedStyle(el)

  // 添加事件委托
  el.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.matches('.rich-overflow-btn')) {
      event.stopPropagation()
      handleExpand()
    }
  })

  const applyButtonStyles = (button: HTMLElement) => {
    if (!options.buttonStyle.fontSize) {
      button.style.fontSize = computedStyle.fontSize
    }
  }

  function handleExpand() {
    const state = elementStateMap.get(el)!
    state.isExpanded = !state.isExpanded

    if (state.isExpanded) {
      // 展开状态 - 使用原始HTML
      el.innerHTML = state.originalText
      const lessBtn = createActionButton(options.lessText, options.buttonClass, options.buttonStyle)
      applyButtonStyles(lessBtn)

      const lastTextNode = findLastTextNode(el)
      if (lastTextNode && lastTextNode.parentNode) {
        lastTextNode.parentNode.appendChild(lessBtn)
      }
      else {
        el.appendChild(lessBtn)
      }
    }
    else {
      // 收起状态 - 使用清理后的HTML
      el.innerHTML = state.cleanedHtml
      handleOverflow()
    }
  }

  function handleOverflow() {
    const state = elementStateMap.get(el)!
    if (state.isExpanded) {
      return
    }

    if (options.collapseMode === 'lines') {
      const container = document.createElement('div')
      container.style.position = 'relative'

      const textContainer = document.createElement('div')
      textContainer.style.display = '-webkit-box'
      textContainer.style.webkitBoxOrient = 'vertical'
      textContainer.style.webkitLineClamp = options.num.toString()
      textContainer.style.overflow = 'hidden'
      textContainer.style.wordBreak = 'break-all'
      // 使用已清理的HTML
      textContainer.innerHTML = state.cleanedHtml

      container.appendChild(textContainer)
      el.innerHTML = ''
      el.appendChild(container)

      // 获取文本容器的实际背景色
      const textContainerStyle = window.getComputedStyle(textContainer)
      const backgroundColor = textContainerStyle.backgroundColor === 'rgba(0, 0, 0, 0)'
        || textContainerStyle.backgroundColor === 'transparent'
        ? 'white'
        : textContainerStyle.backgroundColor

      // 获取显示内容的最后一个元素的样式
      const getLastVisibleElementStyles = () => {
        // 创建一个临时容器来获取实际显示的内容
        const tempContainer = document.createElement('div')
        tempContainer.style.visibility = 'hidden'
        tempContainer.style.position = 'absolute'
        tempContainer.style.height = `${textContainer.clientHeight}px`
        tempContainer.style.width = `${textContainer.clientWidth}px`
        tempContainer.style.overflow = 'hidden'
        tempContainer.innerHTML = textContainer.innerHTML
        document.body.appendChild(tempContainer)

        // 获取所有元素节点
        const allElements = []
        const walker = document.createTreeWalker(
          tempContainer,
          NodeFilter.SHOW_ELEMENT,
          {
            acceptNode: (node) => {
              // 只接受有内容的元素
              if (node.textContent?.trim()) {
                return NodeFilter.FILTER_ACCEPT
              }
              return NodeFilter.FILTER_SKIP
            },
          },
        )

        while (walker.nextNode()) {
          allElements.push(walker.currentNode as Element)
        }

        // 如果没有找到元素，尝试获取文本节点
        if (allElements.length === 0) {
          const textWalker = document.createTreeWalker(
            tempContainer,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) => {
                if (node.textContent?.trim()) {
                  return NodeFilter.FILTER_ACCEPT
                }
                return NodeFilter.FILTER_SKIP
              },
            },
          )

          while (textWalker.nextNode()) {
            if (textWalker.currentNode?.parentElement) {
              allElements.push(textWalker.currentNode.parentElement)
            }
          }
        }

        // 清理临时容器
        document.body.removeChild(tempContainer)

        // 获取最后一个元素的样式
        if (allElements.length > 0) {
          const lastElement = allElements[allElements.length - 1]
          const computedStyle = window.getComputedStyle(lastElement)
          return {
            fontSize: computedStyle.fontSize,
            lineHeight: computedStyle.lineHeight,
          }
        }

        // 默认返回容器的样式
        return {
          fontSize: textContainerStyle.fontSize,
          lineHeight: textContainerStyle.lineHeight,
        }
      }

      const btnContainer = document.createElement('div')
      if (options.buttonContainerClass) {
        btnContainer.classList.add(options.buttonContainerClass)
      }

      btnContainer.style.position = 'absolute'
      btnContainer.style.right = '0'
      btnContainer.style.bottom = '0'
      btnContainer.style.width = '80px'
      btnContainer.style.textAlign = 'right'
      btnContainer.style.background = `linear-gradient(to right, transparent, ${backgroundColor} 40%)`
      btnContainer.style.paddingLeft = '30px'

      Object.assign(btnContainer.style, options.buttonContainerStyle)

      container.appendChild(btnContainer)

      requestAnimationFrame(() => {
        const hasOverflow = textContainer.scrollHeight > textContainer.clientHeight

        if (hasOverflow) {
          const moreBtn = createActionButton(options.moreText, options.buttonClass, options.buttonStyle)
          applyButtonStyles(moreBtn)
          moreBtn.style.display = 'inline-block'
          moreBtn.style.position = 'relative'
          moreBtn.style.zIndex = '1'

          // 应用最后可见元素的样式
          const lastStyles = getLastVisibleElementStyles()
          moreBtn.style.fontSize = moreBtn.style.fontSize || lastStyles.fontSize
          moreBtn.style.lineHeight = moreBtn.style.lineHeight || lastStyles.lineHeight
          moreBtn.style.verticalAlign = 'baseline'

          btnContainer.appendChild(moreBtn)
        }
      })

      return
    }

    if (state.cleanedText.length <= options.num) {
      return
    }

    const numNew = calculateAdjustedLength(state.cleanedText, options.num)

    // 创建 TreeWalker 来遍历文本节点
    const tempDiv = document.createElement('div')
    // 使用已清理的HTML
    tempDiv.innerHTML = state.cleanedHtml

    let currentLength = 0
    let lastNode: Text | null = null
    let lastNodeEndOffset = 0

    // 遍历文本节点直到达到目标长度
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          // 跳过空文本节点
          if (!node.textContent?.trim()) {
            return NodeFilter.FILTER_SKIP
          }
          return NodeFilter.FILTER_ACCEPT
        },
      },
    )

    while (walker.nextNode() && currentLength < numNew) {
      const node = walker.currentNode as Text
      const text = node.textContent || ''

      if (currentLength + text.length > numNew) {
        lastNode = node
        lastNodeEndOffset = numNew - currentLength
        break
      }

      currentLength += text.length
      lastNode = node
      lastNodeEndOffset = text.length
    }

    if (lastNode) {
      // 创建范围来处理文本截断
      const range = document.createRange()
      range.setStart(tempDiv, 0)
      range.setEnd(lastNode, lastNodeEndOffset)

      // 提取截断的内容
      const truncatedContent = range.cloneContents()
      const container = document.createElement('div')
      container.appendChild(truncatedContent)

      // 添加省略号和按钮
      const wrapper = document.createElement('div')
      wrapper.innerHTML = container.innerHTML

      const ellipsisSpan = document.createElement('span')
      ellipsisSpan.textContent = options.ellipsis

      const moreBtn = createActionButton(options.moreText, options.buttonClass, options.buttonStyle)
      applyButtonStyles(moreBtn)

      // 找到最后一个文本节点
      const lastTextNode = findLastTextNode(wrapper)
      if (lastTextNode && lastTextNode.parentNode) {
        lastTextNode.parentNode.appendChild(ellipsisSpan)
        lastTextNode.parentNode.appendChild(moreBtn)
      }
      else {
        wrapper.appendChild(ellipsisSpan)
        wrapper.appendChild(moreBtn)
      }

      el.innerHTML = wrapper.innerHTML
    }
  }

  // 添加一个辅助函数来找到最后一个文本节点
  function findLastTextNode(node: Node): Text | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return node as Text
    }

    for (let i = node.childNodes.length - 1; i >= 0; i--) {
      const lastTextNode = findLastTextNode(node.childNodes[i])
      if (lastTextNode) {
        return lastTextNode
      }
    }

    return null
  }

  // 清理函数
  el._cleanup = () => {
    el.removeEventListener('click', handleExpand)
    window.removeEventListener('resize', handleResize)
    elementStateMap.delete(el) // 清理状态
  }

  // 初始化
  if (options.defaultExpanded) {
    // 如果默认展开，直接显示完整内容
    const state = elementStateMap.get(el)!
    state.isExpanded = true
    el.innerHTML = state.originalText
    const lessBtn = createActionButton(options.lessText, options.buttonClass, options.buttonStyle)
    applyButtonStyles(lessBtn)

    const lastTextNode = findLastTextNode(el)
    if (lastTextNode && lastTextNode.parentNode) {
      lastTextNode.parentNode.appendChild(lessBtn)
    }
    else {
      el.appendChild(lessBtn)
    }
  }
  else {
    handleOverflow()
  }

  // 使用防抖优化 resize 事件处理
  let resizeTimeout: number | null = null
  function handleResize() {
    if (resizeTimeout) {
      window.clearTimeout(resizeTimeout)
    }
    resizeTimeout = window.setTimeout(handleOverflow, 150)
  }

  window.addEventListener('resize', handleResize)
}

export const vRichOverflow = {
  mounted: initialize,

  unmounted(el: CustomHTMLElement) {
    el._cleanup?.()
  },
}
